// useSendFlow.ts - S4 as a result union. Only `sent` refreshes the mapped
// queries; a reprompt or failure must leave history untouched.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { handleApiError } from "@/services/api";
import { applyInvalidations, INVALIDATES } from "../api/invalidates";
import { merchantFlowService } from "../api/merchantFlowService";
import type {
  DocumentRequest,
  DraftAssessment,
  FlowDetail,
  MerchantFlowError,
  SendRequest,
} from "../types";

export type SendFlowResult =
  | { kind: "sent"; flow: FlowDetail }
  | { kind: "reprompt"; assessment: DraftAssessment }
  /** The staff-fill preview needs staff (or DocuSeal) first: keep it open. */
  | { kind: "preview"; message: string }
  /** `uploadGone`: the Custom upload was sent (another tab) or removed meanwhile. */
  | { kind: "error"; message: string; uploadGone?: true };

const PREVIEW_REASONS: ReadonlySet<string> = new Set(["preview_incomplete", "preview_unreachable"]);

export type SendFlowFailure = Exclude<SendFlowResult, { kind: "sent" }>;

/** Shared by the send and the preview mint, which fail the same ways. */
export function classifySendFailure(err: unknown): SendFlowFailure {
  if (!isAxiosError(err)) return { kind: "error", message: "Something went wrong" };
  const body = err.response?.data as MerchantFlowError | undefined;
  if (err.response?.status === 409 && body?.error === "reprompt" && body.assessment) {
    return { kind: "reprompt", assessment: body.assessment };
  }
  if (body?.error === "send_failed" && PREVIEW_REASONS.has(body.reason ?? "")) {
    return { kind: "preview", message: handleApiError(err) };
  }
  if (body?.reason === "document_unavailable") {
    return { kind: "error", message: handleApiError(err), uploadGone: true };
  }
  return { kind: "error", message: handleApiError(err) };
}

export function useSendFlow(borrowerId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: SendRequest | DocumentRequest): Promise<SendFlowResult> => {
      try {
        return { kind: "sent", flow: await merchantFlowService.send(borrowerId, body) };
      } catch (err) {
        return classifySendFailure(err);
      }
    },
    onSuccess: (result) => {
      if (result.kind !== "sent") return;
      applyInvalidations(queryClient, INVALIDATES.send(borrowerId));
    },
  });
}
